import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Briefcase, MapPin, Clock, DollarSign, CheckCircle, 
  ChevronRight, Send, ArrowLeft, HeartHandshake, Building,
  Mail, Phone, User, FileText, Linkedin, Calendar, X,
  GraduationCap, Car, AlertCircle, Users, Plus, Trash2, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EMPLOYMENT_TYPE_LABELS = {
  full_time: 'Full Time',
  part_time: 'Part Time',
  contract: 'Contract',
  temporary: 'Temporary',
  internship: 'Internship'
};

const EDUCATION_LEVELS = [
  { value: 'high_school', label: 'High School / GED' },
  { value: 'some_college', label: 'Some College' },
  { value: 'associates', label: "Associate's Degree" },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'doctorate', label: 'Doctorate' }
];

const SHIFT_OPTIONS = ['Morning', 'Afternoon', 'Evening', 'Overnight', 'Weekends'];

const CareersPage = () => {
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const [form, setForm] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: 'NC',
    zip_code: '',
    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    // Professional
    resume_url: '',
    cover_letter: '',
    linkedin_url: '',
    work_history: [{ company: '', title: '', start_date: '', end_date: '', duties: '', reason_for_leaving: '' }],
    // Education
    education_level: '',
    school_name: '',
    field_of_study: '',
    graduation_year: '',
    // Certifications & License
    certifications: [{ name: '', issuing_org: '', date_obtained: '', expiration_date: '' }],
    has_drivers_license: false,
    drivers_license_state: '',
    drivers_license_number: '',
    has_reliable_transportation: false,
    vehicle_type: '',
    // References
    references: [
      { name: '', phone: '', email: '', relationship: '', company: '' },
      { name: '', phone: '', email: '', relationship: '', company: '' },
      { name: '', phone: '', email: '', relationship: '', company: '' }
    ],
    // Background
    background_check_consent: false,
    has_criminal_history: false,
    criminal_history_explanation: '',
    // Availability
    availability: '',
    available_shifts: [],
    desired_salary: '',
    start_date: '',
    // Additional
    how_heard: '',
    additional_info: '',
    why_interested: '',
    skills: []
  });

  useEffect(() => {
    fetchPostings();
  }, []);

  const fetchPostings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/careers/public/postings`);
      const data = await res.json();
      setPostings(data);
    } catch (error) {
      console.error('Failed to fetch job postings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!form.background_check_consent) {
      toast.error('You must consent to a background check to apply');
      return;
    }
    
    // Filter out empty work history and references
    const cleanedForm = {
      ...form,
      work_history: form.work_history.filter(w => w.company && w.title),
      certifications: form.certifications.filter(c => c.name),
      references: form.references.filter(r => r.name && r.phone)
    };
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/careers/public/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_posting_id: selectedJob.id,
          ...cleanedForm
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to submit application');
      }
      
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  // Work history helpers
  const addWorkHistory = () => {
    setForm(prev => ({
      ...prev,
      work_history: [...prev.work_history, { company: '', title: '', start_date: '', end_date: '', duties: '', reason_for_leaving: '' }]
    }));
  };

  const updateWorkHistory = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      work_history: prev.work_history.map((w, i) => i === index ? { ...w, [field]: value } : w)
    }));
  };

  const removeWorkHistory = (index) => {
    if (form.work_history.length > 1) {
      setForm(prev => ({
        ...prev,
        work_history: prev.work_history.filter((_, i) => i !== index)
      }));
    }
  };

  // Certification helpers
  const addCertification = () => {
    setForm(prev => ({
      ...prev,
      certifications: [...prev.certifications, { name: '', issuing_org: '', date_obtained: '', expiration_date: '' }]
    }));
  };

  const updateCertification = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      certifications: prev.certifications.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const removeCertification = (index) => {
    if (form.certifications.length > 1) {
      setForm(prev => ({
        ...prev,
        certifications: prev.certifications.filter((_, i) => i !== index)
      }));
    }
  };

  // Reference helpers
  const updateReference = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      references: prev.references.map((r, i) => i === index ? { ...r, [field]: value } : r)
    }));
  };

  // Shift toggle
  const toggleShift = (shift) => {
    setForm(prev => ({
      ...prev,
      available_shifts: prev.available_shifts.includes(shift)
        ? prev.available_shifts.filter(s => s !== shift)
        : [...prev.available_shifts, shift]
    }));
  };

  const resetForm = () => {
    setForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      address: '',
      city: '',
      state: 'NC',
      zip_code: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      resume_url: '',
      cover_letter: '',
      linkedin_url: '',
      work_history: [{ company: '', title: '', start_date: '', end_date: '', duties: '', reason_for_leaving: '' }],
      education_level: '',
      school_name: '',
      field_of_study: '',
      graduation_year: '',
      certifications: [{ name: '', issuing_org: '', date_obtained: '', expiration_date: '' }],
      has_drivers_license: false,
      drivers_license_state: '',
      drivers_license_number: '',
      has_reliable_transportation: false,
      vehicle_type: '',
      references: [
        { name: '', phone: '', email: '', relationship: '', company: '' },
        { name: '', phone: '', email: '', relationship: '', company: '' },
        { name: '', phone: '', email: '', relationship: '', company: '' }
      ],
      background_check_consent: false,
      has_criminal_history: false,
      criminal_history_explanation: '',
      availability: '',
      available_shifts: [],
      desired_salary: '',
      start_date: '',
      how_heard: '',
      additional_info: '',
      why_interested: '',
      skills: []
    });
    setSubmitted(false);
    setShowApplicationForm(false);
    setSelectedJob(null);
    setCurrentStep(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-xl flex items-center justify-center">
              <span className="text-black font-bold text-lg">H</span>
            </div>
            <span className="text-xl font-bold text-white font-['Cormorant_Garamond']">NC Help Hub</span>
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-[var(--background-paper)]/10">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold font-['Cormorant_Garamond'] mb-4">
            Join Our <span className="gradient-text">Team</span>
          </h1>
          <p className="text-lg text-[var(--text-muted)] max-w-2xl mx-auto mb-8">
            We're looking for passionate individuals who want to make a difference in people's lives. 
            Explore our open positions and become part of the NC Help Hub family.
          </p>
          
          {/* Quick Apply CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/join">
              <Button className="btn-primary text-lg px-8 py-6 rounded-full" data-testid="quick-apply-btn">
                <Users className="w-5 h-5 mr-2" />
                Apply as a Worker
              </Button>
            </Link>
            <span className="text-[var(--text-muted)]">or</span>
            <a href="#open-positions">
              <Button variant="outline" className="text-lg px-8 py-6 rounded-full border-white/20">
                View Open Positions
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="card p-6 text-center">
            <div className="w-12 h-12 bg-[var(--success)]/15 rounded-full flex items-center justify-center mx-auto mb-3">
              <Briefcase className="w-6 h-6 text-[var(--success)]" />
            </div>
            <p className="text-3xl font-bold text-[var(--primary)]">{postings.length}</p>
            <p className="text-[var(--text-muted)]">Open Positions</p>
          </div>
          <div className="card p-6 text-center">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <MapPin className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[var(--primary)]">Tarboro, NC</p>
            <p className="text-[var(--text-muted)]">Primary Location</p>
          </div>
          <div className="card p-6 text-center">
            <div className="w-12 h-12 bg-[#F4C2C2]/15 rounded-full flex items-center justify-center mx-auto mb-3">
              <HeartHandshake className="w-6 h-6 text-[#F4C2C2]" />
            </div>
            <p className="text-3xl font-bold text-[var(--primary)]">Family</p>
            <p className="text-[var(--text-muted)]">Work Environment</p>
          </div>
        </div>

        {/* Job Listings */}
        <div id="open-positions">
        {postings.length === 0 ? (
          <div className="card p-12 text-center">
            <Briefcase className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Formal Positions Listed</h3>
            <p className="text-[var(--text-muted)] mb-6">
              We don't have any formal job postings right now, but we're always looking for great people!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/join">
                <Button className="btn-primary">Apply as a Worker</Button>
              </Link>
              <Link to="/">
                <Button variant="outline">Return Home</Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold font-['Cormorant_Garamond']">Open Positions</h2>
            {postings.map((posting, index) => (
              <motion.div
                key={posting.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedJob(posting)}
                data-testid={`job-listing-${posting.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[var(--primary)]/10 rounded-lg flex items-center justify-center">
                        <Building className="w-5 h-5 text-[var(--primary)]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{posting.title}</h3>
                        <p className="text-sm text-[var(--text-muted)]">{posting.department}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {posting.location}
                        {posting.is_remote && ' (Remote Available)'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {EMPLOYMENT_TYPE_LABELS[posting.employment_type]}
                      </span>
                      {(posting.salary_min || posting.salary_max) && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {posting.salary_min && posting.salary_max 
                            ? `$${posting.salary_min} - $${posting.salary_max}/${posting.salary_type === 'hourly' ? 'hr' : 'yr'}`
                            : posting.salary_min 
                              ? `From $${posting.salary_min}/${posting.salary_type === 'hourly' ? 'hr' : 'yr'}`
                              : `Up to $${posting.salary_max}/${posting.salary_type === 'hourly' ? 'hr' : 'yr'}`
                          }
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--text-muted)] mt-3 line-clamp-2">{posting.description}</p>
                  </div>
                  <Button className="btn-primary shrink-0">
                    View & Apply <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        </div>

        {/* Why Work With Us */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] text-center mb-8">Why Work With Us?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: HeartHandshake, title: 'Meaningful Work', desc: 'Make a real difference in people\'s lives every day' },
              { icon: Clock, title: 'Flexible Hours', desc: 'Work schedules that fit your life' },
              { icon: Building, title: 'Growth', desc: 'Opportunities to advance your career' },
              { icon: CheckCircle, title: 'Support', desc: 'Training and resources to help you succeed' }
            ].map((item) => (
              <div key={item.title} className="card p-6 text-center">
                <div className="w-12 h-12 bg-[var(--accent)] rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[var(--primary)]" />
                </div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--text-muted)]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="mt-16 card p-8 text-center bg-gradient-to-r from-[var(--primary)]/5 to-[var(--secondary)]/5">
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-4">Have Questions?</h2>
          <p className="text-[var(--text-muted)] mb-6">
            We're happy to answer any questions about our open positions or the application process.
          </p>
          <a href="mailto:nchelphub@outlook.com" className="inline-flex items-center gap-2 text-[var(--primary)] font-medium hover:underline">
            <Mail className="w-5 h-5" />
            nchelphub@outlook.com
          </a>
        </div>
      </main>

      {/* Job Detail Modal */}
      <AnimatePresence>
        {selectedJob && !showApplicationForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedJob(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[var(--background-paper)] border-b p-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-xl font-bold">{selectedJob.title}</h3>
                  <p className="text-sm text-[var(--text-muted)]">{selectedJob.department}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedJob(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
                {/* Job Info */}
                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
                  <span className="flex items-center gap-1 bg-[var(--background-subtle)] px-3 py-1 rounded-full">
                    <MapPin className="w-4 h-4" />
                    {selectedJob.location}
                  </span>
                  <span className="flex items-center gap-1 bg-[var(--background-subtle)] px-3 py-1 rounded-full">
                    <Clock className="w-4 h-4" />
                    {EMPLOYMENT_TYPE_LABELS[selectedJob.employment_type]}
                  </span>
                  {(selectedJob.salary_min || selectedJob.salary_max) && (
                    <span className="flex items-center gap-1 bg-[var(--success)]/15 text-green-700 px-3 py-1 rounded-full">
                      <DollarSign className="w-4 h-4" />
                      {selectedJob.salary_min && selectedJob.salary_max 
                        ? `$${selectedJob.salary_min} - $${selectedJob.salary_max}/${selectedJob.salary_type === 'hourly' ? 'hr' : 'yr'}`
                        : selectedJob.salary_min 
                          ? `From $${selectedJob.salary_min}/${selectedJob.salary_type === 'hourly' ? 'hr' : 'yr'}`
                          : `Up to $${selectedJob.salary_max}/${selectedJob.salary_type === 'hourly' ? 'hr' : 'yr'}`
                      }
                    </span>
                  )}
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h4 className="font-bold mb-2">About This Role</h4>
                  <p className="text-[var(--text-muted)] whitespace-pre-wrap">{selectedJob.description}</p>
                </div>

                {/* Responsibilities */}
                {selectedJob.responsibilities?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold mb-2">Responsibilities</h4>
                    <ul className="space-y-2">
                      {selectedJob.responsibilities.map((item, i) => (
                        <li key={`resp-${i}-${item.slice(0,20)}`} className="flex items-start gap-2 text-[var(--text-muted)]">
                          <CheckCircle className="w-4 h-4 text-[var(--success)] mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Requirements */}
                {selectedJob.requirements?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold mb-2">Requirements</h4>
                    <ul className="space-y-2">
                      {selectedJob.requirements.map((item, i) => (
                        <li key={`req-${i}-${item.slice(0,20)}`} className="flex items-start gap-2 text-[var(--text-muted)]">
                          <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedJob.benefits?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold mb-2">Benefits</h4>
                    <ul className="space-y-2">
                      {selectedJob.benefits.map((item, i) => (
                        <li key={`ben-${i}-${item.slice(0,20)}`} className="flex items-start gap-2 text-[var(--text-muted)]">
                          <CheckCircle className="w-4 h-4 text-[#F4C2C2] mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 bg-[var(--background-paper)] border-t p-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedJob(null)}>Close</Button>
                <Button 
                  onClick={() => setShowApplicationForm(true)} 
                  className="btn-primary"
                  data-testid="apply-now-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Apply Now
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Application Form Modal */}
      <AnimatePresence>
        {showApplicationForm && selectedJob && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={resetForm}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {submitted ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-[var(--success)]/15 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[var(--success)]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Application Submitted!</h3>
                  <p className="text-[var(--text-muted)] mb-6">
                    Thank you for applying for the <strong>{selectedJob.title}</strong> position. 
                    We'll review your application and get back to you soon.
                  </p>
                  <Button onClick={resetForm} className="btn-primary">
                    Back to Job Listings
                  </Button>
                </div>
              ) : (
                <>
                  <div className="sticky top-0 bg-[var(--background-paper)] border-b p-4 z-10">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold">Apply for {selectedJob.title}</h3>
                        <p className="text-sm text-[var(--text-muted)]">{selectedJob.department}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={resetForm}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    {/* Step Indicator */}
                    <div className="flex items-center gap-2">
                      {['Personal', 'Work History', 'Education', 'References', 'Background', 'Review'].map((step, i) => (
                        <div key={step} className="flex items-center">
                          <button
                            onClick={() => setCurrentStep(i + 1)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                              currentStep === i + 1 
                                ? 'bg-[var(--primary)] text-white' 
                                : currentStep > i + 1 
                                  ? 'bg-green-500 text-white'
                                  : 'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                            }`}
                          >
                            {currentStep > i + 1 ? <CheckCircle className="w-4 h-4" /> : i + 1}
                          </button>
                          {i < 5 && <div className={`w-4 h-0.5 ${currentStep > i + 1 ? 'bg-green-500' : 'bg-[var(--background-subtle)]'}`} />}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {/* Step 1: Personal Information */}
                    {currentStep === 1 && (
                      <div className="space-y-6">
                        <h4 className="font-bold text-lg flex items-center gap-2">
                          <User className="w-5 h-5 text-[var(--primary)]" /> Personal Information
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>First Name *</Label>
                            <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} placeholder="John" required />
                          </div>
                          <div>
                            <Label>Last Name *</Label>
                            <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} placeholder="Doe" required />
                          </div>
                          <div>
                            <Label>Email *</Label>
                            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@email.com" required />
                          </div>
                          <div>
                            <Label>Phone *</Label>
                            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(252) 555-0100" required />
                          </div>
                          <div>
                            <Label>Date of Birth</Label>
                            <Input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Street Address" />
                          <div className="grid grid-cols-3 gap-4">
                            <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="City" />
                            <Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} placeholder="State" />
                            <Input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} placeholder="ZIP" />
                          </div>
                        </div>
                        <div className="p-4 bg-[var(--warning)]/10 rounded-lg">
                          <h5 className="font-medium mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Emergency Contact</h5>
                          <div className="grid md:grid-cols-3 gap-4">
                            <Input value={form.emergency_contact_name} onChange={e => setForm({...form, emergency_contact_name: e.target.value})} placeholder="Contact Name" />
                            <Input value={form.emergency_contact_phone} onChange={e => setForm({...form, emergency_contact_phone: e.target.value})} placeholder="Phone" />
                            <Input value={form.emergency_contact_relationship} onChange={e => setForm({...form, emergency_contact_relationship: e.target.value})} placeholder="Relationship" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Work History */}
                    {currentStep === 2 && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-lg flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-[var(--primary)]" /> Work History
                          </h4>
                          <Button type="button" variant="outline" size="sm" onClick={addWorkHistory}>
                            <Plus className="w-4 h-4 mr-1" /> Add Job
                          </Button>
                        </div>
                        {form.work_history.map((job, idx) => (
                          <div key={job.company || `work-${idx}`} className="p-4 border rounded-lg space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">Position {idx + 1}</span>
                              {form.work_history.length > 1 && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeWorkHistory(idx)} className="text-[var(--error)]">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <Input value={job.company} onChange={e => updateWorkHistory(idx, 'company', e.target.value)} placeholder="Company Name" />
                              <Input value={job.title} onChange={e => updateWorkHistory(idx, 'title', e.target.value)} placeholder="Job Title" />
                              <Input type="date" value={job.start_date} onChange={e => updateWorkHistory(idx, 'start_date', e.target.value)} placeholder="Start Date" />
                              <Input type="date" value={job.end_date} onChange={e => updateWorkHistory(idx, 'end_date', e.target.value)} placeholder="End Date" />
                            </div>
                            <Textarea value={job.duties} onChange={e => updateWorkHistory(idx, 'duties', e.target.value)} placeholder="Key responsibilities and duties..." rows={2} />
                            <Input value={job.reason_for_leaving} onChange={e => updateWorkHistory(idx, 'reason_for_leaving', e.target.value)} placeholder="Reason for leaving" />
                          </div>
                        ))}
                        <div className="space-y-4 pt-4 border-t">
                          <Label>Resume URL (Google Drive, Dropbox, etc.)</Label>
                          <Input value={form.resume_url} onChange={e => setForm({...form, resume_url: e.target.value})} placeholder="https://drive.google.com/..." />
                          <Label>LinkedIn Profile</Label>
                          <Input value={form.linkedin_url} onChange={e => setForm({...form, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/yourprofile" />
                        </div>
                      </div>
                    )}

                    {/* Step 3: Education & Certifications */}
                    {currentStep === 3 && (
                      <div className="space-y-6">
                        <h4 className="font-bold text-lg flex items-center gap-2">
                          <GraduationCap className="w-5 h-5 text-[var(--primary)]" /> Education & Certifications
                        </h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>Highest Education Level</Label>
                            <Select value={form.education_level} onValueChange={v => setForm({...form, education_level: v})}>
                              <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                              <SelectContent>
                                {EDUCATION_LEVELS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input value={form.school_name} onChange={e => setForm({...form, school_name: e.target.value})} placeholder="School Name" />
                          <Input value={form.field_of_study} onChange={e => setForm({...form, field_of_study: e.target.value})} placeholder="Field of Study" />
                          <Input value={form.graduation_year} onChange={e => setForm({...form, graduation_year: e.target.value})} placeholder="Graduation Year" />
                        </div>
                        <div className="pt-4 border-t">
                          <div className="flex items-center justify-between mb-4">
                            <h5 className="font-medium">Certifications & Licenses</h5>
                            <Button type="button" variant="outline" size="sm" onClick={addCertification}>
                              <Plus className="w-4 h-4 mr-1" /> Add
                            </Button>
                          </div>
                          {form.certifications.map((cert, idx) => (
                            <div key={cert.name || `cert-${idx}`} className="flex gap-2 mb-2">
                              <Input value={cert.name} onChange={e => updateCertification(idx, 'name', e.target.value)} placeholder="Certification Name" className="flex-1" />
                              <Input value={cert.issuing_org} onChange={e => updateCertification(idx, 'issuing_org', e.target.value)} placeholder="Issuing Org" className="flex-1" />
                              {form.certifications.length > 1 && (
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeCertification(idx)} className="text-[var(--error)]">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="pt-4 border-t">
                          <h5 className="font-medium mb-4 flex items-center gap-2"><Car className="w-4 h-4" /> Driver's License & Transportation</h5>
                          <div className="grid md:grid-cols-2 gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={form.has_drivers_license} onChange={e => setForm({...form, has_drivers_license: e.target.checked})} className="w-4 h-4" />
                              I have a valid driver's license
                            </label>
                            {form.has_drivers_license && (
                              <>
                                <Input value={form.drivers_license_state} onChange={e => setForm({...form, drivers_license_state: e.target.value})} placeholder="License State (e.g., NC)" />
                                <Input value={form.drivers_license_number} onChange={e => setForm({...form, drivers_license_number: e.target.value})} placeholder="License Number (optional)" />
                              </>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={form.has_reliable_transportation} onChange={e => setForm({...form, has_reliable_transportation: e.target.checked})} className="w-4 h-4" />
                              I have reliable transportation
                            </label>
                            {form.has_reliable_transportation && (
                              <Input value={form.vehicle_type} onChange={e => setForm({...form, vehicle_type: e.target.value})} placeholder="Vehicle type (e.g., SUV, Sedan)" />
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 4: References */}
                    {currentStep === 4 && (
                      <div className="space-y-6">
                        <h4 className="font-bold text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-[var(--primary)]" /> Professional References (3 Required)
                        </h4>
                        {form.references.map((ref, idx) => (
                          <div key={ref.name || `ref-${idx}`} className="p-4 border rounded-lg space-y-4">
                            <span className="font-medium">Reference {idx + 1}</span>
                            <div className="grid md:grid-cols-2 gap-4">
                              <Input value={ref.name} onChange={e => updateReference(idx, 'name', e.target.value)} placeholder="Full Name *" />
                              <Input value={ref.phone} onChange={e => updateReference(idx, 'phone', e.target.value)} placeholder="Phone Number *" />
                              <Input value={ref.email} onChange={e => updateReference(idx, 'email', e.target.value)} placeholder="Email" />
                              <Input value={ref.relationship} onChange={e => updateReference(idx, 'relationship', e.target.value)} placeholder="Relationship (e.g., Supervisor)" />
                              <Input value={ref.company} onChange={e => updateReference(idx, 'company', e.target.value)} placeholder="Company" className="md:col-span-2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Step 5: Background Check */}
                    {currentStep === 5 && (
                      <div className="space-y-6">
                        <h4 className="font-bold text-lg flex items-center gap-2">
                          <Shield className="w-5 h-5 text-[var(--primary)]" /> Background Check & Disclosure
                        </h4>
                        <div className="p-4 bg-[#D4AF37]/10 rounded-lg space-y-4">
                          <p className="text-sm text-[var(--text-muted)]">
                            NC Help Hub requires all employees to undergo a background check as a condition of employment. 
                            This is to ensure the safety of our clients and team members.
                          </p>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={form.background_check_consent} 
                              onChange={e => setForm({...form, background_check_consent: e.target.checked})} 
                              className="w-5 h-5 mt-0.5" 
                            />
                            <span className="text-sm">
                              <strong>I consent to a background check *</strong>
                              <br />
                              I understand that my offer of employment is contingent upon the successful completion of a background check.
                            </span>
                          </label>
                        </div>
                        <div className="space-y-4">
                          <Label>Have you ever been convicted of a crime?</Label>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="criminal" checked={form.has_criminal_history === false} onChange={() => setForm({...form, has_criminal_history: false})} />
                              No
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="criminal" checked={form.has_criminal_history === true} onChange={() => setForm({...form, has_criminal_history: true})} />
                              Yes
                            </label>
                          </div>
                          {form.has_criminal_history && (
                            <Textarea 
                              value={form.criminal_history_explanation} 
                              onChange={e => setForm({...form, criminal_history_explanation: e.target.value})} 
                              placeholder="Please provide details. A criminal record does not automatically disqualify you from employment."
                              rows={3}
                            />
                          )}
                        </div>
                        <div className="pt-4 border-t space-y-4">
                          <h5 className="font-medium flex items-center gap-2"><Calendar className="w-4 h-4" /> Availability</h5>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label>When can you start?</Label>
                              <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
                            </div>
                            <div>
                              <Label>Desired Salary</Label>
                              <Input value={form.desired_salary} onChange={e => setForm({...form, desired_salary: e.target.value})} placeholder="e.g., $15-20/hr" />
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block">Available Shifts</Label>
                            <div className="flex flex-wrap gap-2">
                              {SHIFT_OPTIONS.map(shift => (
                                <button
                                  key={shift}
                                  type="button"
                                  onClick={() => toggleShift(shift)}
                                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                                    form.available_shifts.includes(shift) 
                                      ? 'bg-[var(--primary)] text-white border-[var(--primary)]' 
                                      : 'bg-[var(--background-paper)] border-[var(--border-primary)] hover:border-[var(--primary)]'
                                  }`}
                                >
                                  {shift}
                                </button>
                              ))}
                            </div>
                          </div>
                          <Textarea value={form.availability} onChange={e => setForm({...form, availability: e.target.value})} placeholder="Additional availability notes..." rows={2} />
                        </div>
                      </div>
                    )}

                    {/* Step 6: Review & Submit */}
                    {currentStep === 6 && (
                      <div className="space-y-6">
                        <h4 className="font-bold text-lg flex items-center gap-2">
                          <FileText className="w-5 h-5 text-[var(--primary)]" /> Final Questions & Review
                        </h4>
                        <div>
                          <Label>Why are you interested in this position?</Label>
                          <Textarea value={form.why_interested} onChange={e => setForm({...form, why_interested: e.target.value})} placeholder="Tell us what excites you about this role..." rows={3} />
                        </div>
                        <div>
                          <Label>Cover Letter</Label>
                          <Textarea value={form.cover_letter} onChange={e => setForm({...form, cover_letter: e.target.value})} placeholder="Write a brief cover letter..." rows={4} />
                        </div>
                        <div>
                          <Label>How did you hear about us?</Label>
                          <Input value={form.how_heard} onChange={e => setForm({...form, how_heard: e.target.value})} placeholder="e.g., Indeed, Friend referral, Social media" />
                        </div>
                        <div>
                          <Label>Anything else you'd like us to know?</Label>
                          <Textarea value={form.additional_info} onChange={e => setForm({...form, additional_info: e.target.value})} placeholder="Any additional information..." rows={3} />
                        </div>
                        <div className="p-4 bg-[var(--success)]/10 rounded-lg">
                          <h5 className="font-medium mb-2">Application Summary</h5>
                          <div className="text-sm text-[var(--text-muted)] space-y-1">
                            <p><strong>Name:</strong> {form.first_name} {form.last_name}</p>
                            <p><strong>Email:</strong> {form.email}</p>
                            <p><strong>Phone:</strong> {form.phone}</p>
                            <p><strong>Work History:</strong> {form.work_history.filter(w => w.company).length} position(s) listed</p>
                            <p><strong>References:</strong> {form.references.filter(r => r.name).length} reference(s) provided</p>
                            <p><strong>Background Check:</strong> {form.background_check_consent ? '✓ Consent given' : '✗ Consent required'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="sticky bottom-0 bg-[var(--background-paper)] border-t p-4 flex justify-between">
                    <Button variant="outline" onClick={() => currentStep === 1 ? setShowApplicationForm(false) : setCurrentStep(currentStep - 1)}>
                      {currentStep === 1 ? 'Cancel' : 'Previous'}
                    </Button>
                    {currentStep < 6 ? (
                      <Button onClick={() => setCurrentStep(currentStep + 1)} className="btn-primary">
                        Next Step
                      </Button>
                    ) : (
                      <Button onClick={handleSubmitApplication} disabled={submitting || !form.background_check_consent} className="btn-primary" data-testid="submit-application-btn">
                        {submitting ? 'Submitting...' : <><Send className="w-4 h-4 mr-2" /> Submit Application</>}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="bg-[var(--background-paper)] border-t py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-[var(--text-muted)]">
          <p>&copy; {new Date().getFullYear()} NC Help Hub. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/" className="hover:text-[var(--primary)]">Home</Link>
            {' | '}
            <a href="mailto:nchelphub@outlook.com" className="hover:text-[var(--primary)]">Contact</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
